import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DataGrid, { Paging, Pager, Column, Grouping } from 'devextreme-react/data-grid';
import {
    ExperienceVerificationStatusType,
    ExperienceProjectType,
    ExperiencePropertyTypeOptions,
    GridSettingType,
} from '@apply/constants';
import { ExperienceApi } from '@apply/api';
import { RefreshEvents } from '@apply/ui';
import { applicationInsights } from '../../ApplicationInsights';
import { Tooltip } from 'devextreme-react/tooltip';
import {
    executeNonBFIExperienceAction,
    onNonBFIExperienceActionCellRender,
    onCityCellRender,
    onStateCellRender,
} from '../common/ExperienceFunctions';
import { formatCurrency, formatDate, handleAdminError } from '../../common/SharedFunctions';
import { NonBFIExperienceActionType } from '../common/ExperienceConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';

const NonBfiTableFilter = ({ onChange, returnedVerificationTypes }) => {
    const [verificationStatusTypeFilterSubmitted, set_verificationStatusTypeFilterSubmitted] = useState(true);
    const [verificationStatusTypeFilterVerified, set_verificationStatusTypeFilterVerified] = useState(true);
    const [verificationStatusTypeFilterUnableToVerify, set_verificationStatusTypeFilterUnableToVerify] = useState(true);
    const [verificationStatusTypeFilterRejected, set_verificationStatusTypeFilterRejected] = useState(true);

    const styles = {
        typeFilter: {
            display: 'inline-block',
            width: 'auto',
            textAlign: 'left',
            marginLeft: '10px',
            marginRight: '10px',
        },
        notActiveTypeFilter: {
            display: 'inline-block',
            width: 'auto',
            textAlign: 'left',
            marginLeft: '10px',
            marginRight: '10px',
            color: 'silver',
        },
    };

    const existsInReturnedVerificationTypes = (vt) => {
        const found = returnedVerificationTypes.find((g) => g.id === vt);
        return found;
    };

    useEffect(() => {
        const submittedType = [ExperienceVerificationStatusType.Submitted];
        const verifiedType = [ExperienceVerificationStatusType.Verified];
        const unableToVerifyType = [ExperienceVerificationStatusType.UnableToVerify];
        const rejectedType = [ExperienceVerificationStatusType.Rejected];
        let verificationStatusTypes = verificationStatusTypeFilterSubmitted ? submittedType : [];
        if (verificationStatusTypeFilterVerified) verificationStatusTypes = [...verificationStatusTypes, ...verifiedType];
        if (verificationStatusTypeFilterUnableToVerify)
            verificationStatusTypes = [...verificationStatusTypes, ...unableToVerifyType];
        if (verificationStatusTypeFilterRejected) verificationStatusTypes = [...verificationStatusTypes, ...rejectedType];

        const filter = {
            verificationStatusTypes,
        };
        onChange && onChange(filter);
    }, [
        verificationStatusTypeFilterSubmitted,
        verificationStatusTypeFilterVerified,
        verificationStatusTypeFilterUnableToVerify,
        verificationStatusTypeFilterRejected,
    ]);

    return (
        <div style={{ margin: '10px' }}>
            <span>
                Status:&nbsp;&nbsp;
                <span
                    style={
                        existsInReturnedVerificationTypes(ExperienceVerificationStatusType.Submitted)
                            ? styles.typeFilter
                            : styles.notActiveTypeFilter
                    }
                >
                    <input
                        type='checkbox'
                        value={0}
                        checked={verificationStatusTypeFilterSubmitted}
                        onChange={(e) => set_verificationStatusTypeFilterSubmitted(e.target.checked)}
                    />{' '}
                    Submitted
                </span>
                <span
                    style={
                        existsInReturnedVerificationTypes(ExperienceVerificationStatusType.Verified)
                            ? styles.typeFilter
                            : styles.notActiveTypeFilter
                    }
                >
                    <input
                        type='checkbox'
                        value={1}
                        checked={verificationStatusTypeFilterVerified}
                        onChange={(e) => set_verificationStatusTypeFilterVerified(e.target.checked)}
                    />{' '}
                    Verified{' '}
                </span>
                <span
                    style={
                        existsInReturnedVerificationTypes(ExperienceVerificationStatusType.UnableToVerify)
                            ? styles.typeFilter
                            : styles.notActiveTypeFilter
                    }
                >
                    <input
                        type='checkbox'
                        value={2}
                        checked={verificationStatusTypeFilterUnableToVerify}
                        onChange={(e) => set_verificationStatusTypeFilterUnableToVerify(e.target.checked)}
                    />{' '}
                    Unable to Verify{' '}
                </span>
                <span
                    style={
                        existsInReturnedVerificationTypes(ExperienceVerificationStatusType.Rejected)
                            ? styles.typeFilter
                            : styles.notActiveTypeFilter
                    }
                >
                    <input
                        type='checkbox'
                        value={3}
                        checked={verificationStatusTypeFilterRejected}
                        onChange={(e) => set_verificationStatusTypeFilterRejected(e.target.checked)}
                    />{' '}
                    Rejected{' '}
                </span>
            </span>
        </div>
    );
};

const NonBfiTable = ({ principalId }) => {
    const searchState = useSelector((state) => state.searchState);
    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const [filter, set_filter] = useState(null);
    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = 25;

    const [data, set_data] = useState(null);
    const [criteria, set_criteria] = useState({
        verificationStatusTypes: [],
        searchText: null,
    });
    const [groups, set_groups] = useState([]);

    const criteriaChanged = (vST, searchText) => {
        const newCriteria = {
            verificationStatusTypes: vST,
            searchText,
        };

        const oldCriteria = criteria;
        if (oldCriteria) {
            const oldJson = JSON.stringify(oldCriteria);
            const newJson = JSON.stringify(newCriteria);
            if (oldJson === newJson) {
                return;
            }
        }

        set_criteria(newCriteria);
    };

    const getDataAsync = async (criteria) => {
        if (!criteria || !criteria.verificationStatusTypes || !criteria.verificationStatusTypes.length === 0) {
            return undefined;
        }

        try {
            const response = await experienceApi.getExperiencesNonBfiGrid(principalId, criteria);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve data!');
            }
            const records = response.result.data;

            //Get group Ids and Names
            const grps = Object.values(
                records.reduce((map, { verificationStatusTypeId }) => {
                    map[verificationStatusTypeId] ??= {
                        id: verificationStatusTypeId,
                        name: getVerfiicationTypeName(verificationStatusTypeId),
                    };
                    return map;
                }, {})
            );
            set_groups(grps);

            set_data(records);
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve records! Please refresh the page and try again.');
        }
    };

    const refreshData = async () => {
        await getDataAsync(criteria);
    };

    useEffect(() => {
        const refreshEvents = new RefreshEvents();
        refreshEvents.addRefreshListener(refreshData);

        getDataAsync(criteria);

        return () => {
            refreshEvents.removeRefreshListener(refreshData);
        };
    }, [criteria]);

    useEffect(() => {
        if (!principalId || !filter) {
            return undefined;
        }
        criteriaChanged(filter.verificationStatusTypes, searchState?.searchText?.experience);
    }, [principalId, filter, searchState]);

    const onFilterChange = (fltr) => {
        set_filter(fltr);
    };

    const onHighlightCellRender = (e) => {
        if (!e.value) {
            return null;
        }
        const targetId = 'exp' + e.data.id;
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    <span
                        className='reddot'
                        style={{ position: 'unset' }}
                    ></span>
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Experience is pending BFI verification
                </Tooltip>
            </>
        );
    };

    const onRenderVerificationStatusType = (e) => {
        const grp = groups.find((g) => g.id === e.data.key);
        if (!grp) {
            return null;
        }
        return <div>{grp.name}</div>;
    };

    const getVerfiicationTypeName = (vsTypeId) => {
        switch (vsTypeId) {
            case ExperienceVerificationStatusType.Submitted:
                return 'Submitted';
            case ExperienceVerificationStatusType.Verified:
                return 'Verified';
            case ExperienceVerificationStatusType.UnableToVerify:
                return 'Unable to Verify';
            case ExperienceVerificationStatusType.Rejected:
                return 'Rejected';
            default:
                return 'Unable to Determine';
        }
    };

    const onProjectTypeNameRender = (e) => {
        if (e.data.projectTypeId === ExperienceProjectType.FixFlipRehab) {
            if (e.data.budget && e.data.budget >= 100000) {
                return `${e.value} (Major)`;
            } else {
                return e.value;
            }
        } else {
            return e.value;
        }
    };

    const onPropertyTypeRender = (e) => {
        const propertyType = ExperiencePropertyTypeOptions.find((a) => a.id === e.data.propertyTypeId);
        return propertyType?.name;
    };

    const onRenderDateSold = (e) => {
        if (e.data.stillOwn === null || e.data.stillOwn === undefined) {
            return (
                <FontAwesomeIcon
                    icon={faQuestion}
                    className='blue-text'
                />
            );
        } else if (!e.data.stillOwn) {
            if (e.data.dateSold) {
                return formatDate(e.data.dateSold);
            } else {
                return (
                    <FontAwesomeIcon
                        icon={faQuestion}
                        className='blue-text'
                    />
                );
            }
        } else {
            return 'Still Own';
        }
    };

    const onRenderOwned = (e) => {
        if (e.data.monthsOutstanding) {
            let yearsOutstanding = (e.data.monthsOutstanding / 12).toFixed(2);
            if (yearsOutstanding <= 0) {
                return '0 yrs';
            } else if (yearsOutstanding <= 1) {
                return formatYearsOutstanding(yearsOutstanding, true) + ' yr';
            } else {
                return formatYearsOutstanding(yearsOutstanding, false) + ' yrs';
            }
        }
        return null;
    };

    const formatYearsOutstanding = (yearsOutstanding, removeLeadingCharacter) => {
        let startIndex = 0;
        if (removeLeadingCharacter) {
            startIndex = 1;
        }
        if (yearsOutstanding.endsWith(0)) {
            return yearsOutstanding.substring(startIndex, yearsOutstanding.length - 1);
        } else {
            return yearsOutstanding.substring(startIndex, yearsOutstanding.length);
        }
    };

    const onRenderCurrency = (e) => {
        if (!e.value) {
            return null;
        }
        return formatCurrency(e.value, 0);
    };

    if (!principalId || !data) {
        return null;
    }

    return (
        <>
            <NonBfiTableFilter
                onChange={onFilterChange}
                returnedVerificationTypes={groups}
            />
            <DataGrid
                keyExpr='id'
                gridType={GridSettingType.ExperiencesNonBfiTable}
                dataSource={data}
                remoteOperations={false}
                applicationInsights={applicationInsights}
                rowAlternationEnabled={true}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                noDataText='No Non-BFI Experience found for this Principal'
                onRowDblClick={(e) =>
                    executeNonBFIExperienceAction({
                        id: NonBFIExperienceActionType.ModifyNonBfi,
                        experienceId: e.data.id,
                        principalId: e.data.principalId,
                    })
                }
            >
                <Grouping autoExpandAll={true} />
                <Column
                    dataField='verificationStatusTypeId'
                    caption='Status'
                    groupCellRender={onRenderVerificationStatusType}
                    groupIndex={0}
                />
                <Column
                    dataField='highlight'
                    caption=''
                    width={20}
                    cellRender={onHighlightCellRender}
                    alignment='center'
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                />
                <Column
                    dataField='projectTypeName'
                    caption='Project Type'
                    alignment='left'
                    width={200}
                    cellRender={onProjectTypeNameRender}
                />
                <Column
                    dataField='propertyTypeId'
                    caption='Property Type'
                    alignment='left'
                    width={200}
                    cellRender={onPropertyTypeRender}
                />
                <Column
                    dataField='city'
                    caption='City'
                    width={200}
                    cellRender={onCityCellRender}
                />
                <Column
                    dataField='state'
                    caption='State'
                    width={100}
                    cellRender={onStateCellRender}
                />
                <Column
                    caption='Date Sold'
                    width={100}
                    cellRender={onRenderDateSold}
                    alignment='center'
                />
                <Column
                    dataField='salesPrice'
                    caption='Sales Price'
                    width={100}
                    cellRender={onRenderCurrency}
                    alignment='left'
                />
                <Column
                    dataField='monthsOutstanding'
                    caption='Owned'
                    width={60}
                    alignment='left'
                    cellRender={onRenderOwned}
                />
                <Column
                    dataField='roleTypeName'
                    caption='Role'
                    width={150}
                />
                <Column
                    dataField='lender'
                    caption='Lender'
                    alignment='left'
                />
                <Column
                    dataField='unitCount'
                    caption='Units'
                    width={60}
                    alignment='left'
                />
                <Column
                    dataField='credits'
                    caption='Crdts'
                    alignment='left'
                    width={50}
                />
                <Column
                    alignment='center'
                    cellRender={onNonBFIExperienceActionCellRender}
                    width={50}
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                    type='actions'
                    fixed={true}
                    fixedPosition={'right'}
                />
                <Paging
                    defaultPageSize={defaultPageSize}
                    enabled={defaultPageSize > 0}
                />
                <Pager
                    showPageSizeSelector={allowedPageSizes.length > 1}
                    allowedPageSizes={allowedPageSizes}
                    showNavigationButtons={true}
                    showInfo={true}
                    visible={true}
                />
            </DataGrid>
        </>
    );
};

export default NonBfiTable;
